import React, { useCallback, useEffect, useRef } from "react";
import { useChatContext } from "@src/features/assistantChat/ChatRuntimeProvider";
import { useToast } from "@src/lib/hooks/useToast";
import { useAIAssistBrandSelector } from "@src/features/aiAssist/hooks/useAIAssistBrandSelector";
import { useStartGenCourseFromOutlineChatWorkflowMutation } from "@src/features/assistantChat/aiChat.generated";
import useTrackClientFacingWorkflow from "@src/features/app/hooks/useTrackClientFacingWorkflow";
import ChatCanvasDialogContainer from "./ChatCanvasDialogContainer";
import { AiChatType } from "@src/types.generated";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import {
  COURSE_OUTLINE_TITLE,
  COURSE_OUTLINE_DESCRIPTION,
} from "./NewCourseDialog";
import { gql } from "@apollo/client";
import { CreateAiCourseResultFragment } from "./CourseOutlineCanvasDialog.generated";

type Props = {
  dialogIsOpen: boolean;
  setDialogIsOpen: (open: boolean) => void;
  onCourseCreated: (course: CreateAiCourseResultFragment) => void;
};

const CourseOutlineCanvasDialog: React.FC<Props> = ({
  dialogIsOpen,
  setDialogIsOpen,
  onCourseCreated,
}) => {
  const { aiSessionId } = useChatContext();
  const { addErrorToast } = useToast();
  const { selectedSession } = useChatSidebar(AiChatType.CourseOutline);

  const redirectedRef = useRef(false);
  const handleCourseCreated = useCallback(
    (course: CreateAiCourseResultFragment) => {
      if (redirectedRef.current) return;
      redirectedRef.current = true;
      onCourseCreated(course);
      setDialogIsOpen(false);
    },
    [onCourseCreated, setDialogIsOpen],
  );

  const { trackClientFacingWorkflow, isTracking, clientFacingWorkflow } =
    useTrackClientFacingWorkflow({
      onCompleted: (cfw) => {
        if (cfw.result?.libraryItem?.course?.libraryItem) {
          handleCourseCreated(cfw.result.libraryItem.course);
        }
      },
      onError: () => {
        addErrorToast({
          callsite: "course_generation_workflow_failed",
        });
      },
    });

  const shellCourse = clientFacingWorkflow?.result?.libraryItem?.course;
  useEffect(() => {
    if (isTracking && shellCourse?.libraryItem) {
      handleCourseCreated(shellCourse);
    }
  }, [isTracking, shellCourse, handleCourseCreated]);

  const [
    startCourseGenerationWorkflow,
    { loading: generateCourseMutationLoading },
  ] = useStartGenCourseFromOutlineChatWorkflowMutation({
    onCompleted: (data) => {
      const workflow =
        data.startGenCourseFromOutlineChatWorkflow.clientFacingWorkflow;
      if (data.startGenCourseFromOutlineChatWorkflow.success && workflow) {
        redirectedRef.current = false;
        trackClientFacingWorkflow(workflow);
      } else {
        addErrorToast({
          callsite: "start_course_generation_workflow_from_ai_chat",
        });
      }
    },
    onError: () => {
      addErrorToast({
        callsite: "start_course_generation_workflow_from_ai_chat",
      });
    },
  });

  const brandSelectorProps = useAIAssistBrandSelector();

  return (
    <ChatCanvasDialogContainer
      open={dialogIsOpen}
      chatType={AiChatType.CourseOutline}
      setDialogIsOpen={setDialogIsOpen}
      actionOnClick={() => {
        startCourseGenerationWorkflow({
          variables: {
            input: {
              aiChatId: aiSessionId,
              courseOutlineText: selectedSession?.artifact || "",
              brandDetailsId:
                brandSelectorProps.selectedBrandItem?.brandDetailsId,
            },
          },
        });
      }}
      actionLabel="Generate course"
      actionLoading={generateCourseMutationLoading || isTracking}
      canvasSubtitle="Course outline"
      threadEmptyStateTitle={COURSE_OUTLINE_TITLE}
      threadEmptyStateSubtitle={COURSE_OUTLINE_DESCRIPTION}
      composerPlaceholder="Describe your course..."
      brandSelectorProps={brandSelectorProps}
    />
  );
};

export default CourseOutlineCanvasDialog;

gql`
  fragment CreateAICourseResult on Course {
    id
    draftVersion {
      id
    }
    libraryItem {
      id
    }
  }
`;
